import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges,
  Output, SimpleChanges, inject, signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { AuthService } from '../../services/auth.service';

/**
 * Cambio de contraseña de la propia cuenta, siempre con la contraseña actual (folio 32D494).
 *
 * Lo abren tres pantallas:
 *   - Inicio de sesión y Bloqueo de pantalla, cuando el API exige el cambio porque la cuenta
 *     es nueva o el administrador le asignó la contraseña (403 con el identificador
 *     CAMBIO_DE_CONTRASENA). Ahí la actual llega precargada.
 *   - El menú de la cuenta, para el cambio voluntario.
 *
 * Quien lo abre decide qué sigue cuando el cambio queda guardado (`cambiada`): el API cierra
 * las sesiones de la cuenta, así que hay que volver a entrar con la contraseña nueva.
 */
@Component({
  selector: 'app-cambiar-contrasena-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, DialogModule, ButtonModule],
  templateUrl: './cambiar-contrasena-dialog.component.html',
  styleUrls: ['./cambiar-contrasena-dialog.component.scss'],
})
export class CambiarContrasenaDialogComponent implements OnChanges {
  private readonly authService = inject(AuthService);
  private readonly cdr = inject(ChangeDetectorRef);

  /** Lo que cabe en la contraseña de la cuenta. */
  readonly maximo = 15;

  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  /** Usuario o correo de la cuenta; aquí sólo se muestra. */
  @Input() cuenta = '';
  /** Por qué se exige el cambio. Vacío en el cambio voluntario. */
  @Input() aviso = '';
  /** La contraseña con la que se acaba de intentar entrar, para no pedirla dos veces. */
  @Input() contrasenaActual = '';
  /** Se emite con la contraseña nueva cuando el cambio quedó guardado. */
  @Output() cambiada = new EventEmitter<string>();

  actual = '';
  nueva = '';
  confirmar = '';
  readonly verActual = signal(false);
  readonly verNueva = signal(false);
  readonly verConfirmar = signal(false);
  readonly guardando = signal(false);
  readonly error = signal('');

  /**
   * Cada vez que se abre empieza limpio. Se hace aquí y no en un setter de `visible`:
   * en ngOnChanges ya llegaron todas las entradas, también la contraseña precargada.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.actual = this.contrasenaActual ?? '';
      this.nueva = '';
      this.confirmar = '';
      this.verActual.set(false);
      this.verNueva.set(false);
      this.verConfirmar.set(false);
      this.guardando.set(false);
      this.error.set('');
    }
  }

  guardar(): void {
    if (this.guardando()) return;
    const problema = this._revisar();
    if (problema) {
      this.error.set(problema);
      return;
    }
    this.error.set('');
    this.guardando.set(true);
    const nueva = this.nueva;
    this.authService.changePassword(this.cuenta, this.actual, nueva, this.confirmar).subscribe({
      next: () => {
        this.guardando.set(false);
        this.cerrar();
        this.cambiada.emit(nueva);
      },
      error: (err) => {
        this.guardando.set(false);
        const m = err?.error?.message;
        this.error.set((Array.isArray(m) ? m[0] : m) || 'No se pudo cambiar la contraseña. Intenta de nuevo.');
      },
    });
  }

  cerrar(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.cdr.markForCheck();
  }

  /** Las mismas reglas que revisa el API, para avisar sin ir al servidor. */
  private _revisar(): string {
    if (!this.actual) return 'Escribe tu contraseña actual.';
    if (!this.nueva) return 'Escribe la contraseña nueva.';
    if (this.nueva.length > this.maximo) return `La contraseña no puede pasar de ${this.maximo} caracteres.`;
    if (this.nueva !== this.confirmar) return 'La confirmación no coincide con la contraseña nueva.';
    if (this.nueva === this.actual) return 'La contraseña nueva debe ser distinta de la actual.';
    return '';
  }
}
